import Button from "@/components/forms/Button/Button";
import IconConnectSellerAccount from "@/components/icons/IconConnectSellerAccount";
import IconArrow from "@/components/icons/IconArrow";
import styles from "./seller-account-banner.module.css";
import { User, useUserContext } from "@/contexts/UserContext";
import { useLocation } from "wouter";
import { useEffect } from "react";
import { getSyncStatus } from "@/api/fulfillment";
import { PaymentStatus, SyncJobStatus } from "@boxem/core-api-schema";
import IconSyncInventory from "@/components/icons/IconSyncInventory";
import Flex from "../Flex/Flex";
import LoadingSpinner from "../LoadingSpinner/LoadingSpinner";

export type CollapsedSyncStatus = "NEVER_STARTED" | "ERROR" | "IN_PROGRESS" | "COMPLETE";

const collapseSyncJobStatuses = (statuses: SyncJobStatus[]): [CollapsedSyncStatus, number] => {
  const { allSyncJobStatus, allCompletedCount } = statuses.reduce((acc, status) => {
    let collapsedJobStatus: CollapsedSyncStatus = "NEVER_STARTED";
    let completedJobCount = 0;
    if (status === SyncJobStatus.PROCESSING) {
      collapsedJobStatus = "IN_PROGRESS";
    } else if (status === SyncJobStatus.TERMINATED_WITH_ERROR) {
      collapsedJobStatus = "ERROR";
    } else if (status === SyncJobStatus.COMPLETED) {
      collapsedJobStatus = "COMPLETE";
      completedJobCount = 1;
    }
    acc.allSyncJobStatus.push(collapsedJobStatus);
    acc.allCompletedCount += completedJobCount;
    return acc;
  }, { allSyncJobStatus: [] as CollapsedSyncStatus[], allCompletedCount: 0 });

  if (allSyncJobStatus.includes("ERROR")) {
    return ["ERROR", allCompletedCount];
  } else if (allSyncJobStatus.includes("IN_PROGRESS")) {
    return ["IN_PROGRESS", allCompletedCount];
  } else if (allSyncJobStatus.every(status => status === "COMPLETE")) {
    return ["COMPLETE", allCompletedCount];
  } else {
    return ["COMPLETE", allCompletedCount];
  }
}

export const collapseInventorySyncStatus = (seller: User): [CollapsedSyncStatus, number] => {
  if (!seller || !seller.sellerId || seller.sellerId === "") return ["NEVER_STARTED", 0];
  return collapseSyncJobStatuses([seller.shipmentSyncStatus, seller.listingSyncStatus]);
}
export const collaplseSalesSyncStatus = (seller: User): [CollapsedSyncStatus, number] => {
  if (!seller || !seller.sellerId || seller.sellerId === "") return ["NEVER_STARTED", 0];
  return collapseSyncJobStatuses([seller.allSalesSyncStatus]);
}

export const collapseAllSyncStatus = (seller: User): [CollapsedSyncStatus, number] => {
  if (!seller || !seller.sellerId || seller.sellerId === "") return ["NEVER_STARTED", 0]; // the user has never succesfully initiated a sync before
  // Note: We only include shipment and listing sync statuses here to match the backend logic
  // allSalesSyncStatus already excludes estimated fees, so it only represents orders and finance sync
  return collapseSyncJobStatuses([seller.shipmentSyncStatus, seller.listingSyncStatus, seller.allSalesSyncStatus ]);

}

// needs to be an object so the lambdas don't capture 'null' and always use that value
let syncPollInterval = { id: null };
export default function SellerAccountBanner(props) {
  const { user, seller, setSeller, resyncSellerData } = useUserContext();
  const [location, navigate] = useLocation();
  const [status, completedCount] = collapseAllSyncStatus(seller);

  useEffect(() => {
    if (seller && seller.sellerId && status !== "COMPLETE") {
      clearInterval(syncPollInterval.id);

      const poll = () => {
        getSyncStatus().then(syncStatus => {
          console.log("polling sync status", syncStatus);
          const [collapsed, _] = collapseAllSyncStatus(syncStatus as User);
          if (collapsed === "COMPLETE") {
            clearInterval(syncPollInterval.id);
            setSeller({ ...seller, ...syncStatus }, true); // we want to get the sellers' data now.
            return;
          }
          setSeller({ ...seller, ...syncStatus });
        });
      };

      syncPollInterval.id = setInterval(poll, 30000); // 30 seconds
      return () => clearInterval(syncPollInterval.id);
    }
  }, [seller, status]);

  if (!seller || !seller.isSubscribed || !seller.emailVerified || seller.paymentStatus === PaymentStatus.PAST_DUE || (!!seller.sellerId && status === "COMPLETE")) {
    return <></>;
  }

  return (
    <Flex className={styles.connectBanner} justify="space-between" align="center">
      <IconConnectSellerAccount />

      {status === "ERROR" && (
        <Flex direction="column" gap={2} style={{ width: "100%", marginLeft: "20px" }}>
          <p className="inverse-strong">There was an error syncing your data with Amazon.</p>
          <Button
            style={{ maxWidth: "20rem" }}
            type="danger"
            onClick={() => {
              resyncSellerData();
            }}
          >
            <IconSyncInventory></IconSyncInventory>
            <span style={{ marginLeft: "1ch" }}>Re-Sync with Amazon</span>
          </Button>
          <p className="inverse-weak">If the issue persists, contact us.</p>
        </Flex>
      )}

      {status === "IN_PROGRESS" && (
        <Flex direction="column" gap={2} style={{ width: "100%", marginLeft: "20px" }}>
          <Flex gap={2} align="center">
            <p className="inverse-strong">We're syncing your seller data. ({completedCount}/3)</p>
            <LoadingSpinner />
          </Flex>
          <p className="inverse-weak">This page updates in real-time.</p>
        </Flex>
      )}

      {status === "NEVER_STARTED" && (
        <>
          <Flex direction="column" gap={2}>
            <p className="inverse-strong">Connect your Amazon Seller Account</p>
            <p className="inverse-weak">Skyrocket your shipment process by connecting to your Amazon Seller Account.</p>
          </Flex>

          <Flex gap={2}>
            <Button
              onClick={() => {
                navigate("~/connect/seller-api");
              }}
            >
              Connect <IconArrow />
            </Button>
          </Flex>
        </>
      )}
    </Flex>
  );
}
